import { Link } from 'react-router-dom';
import { shell } from '../../shell/shellClasses';
import type { OpsActionItem, OpsPriority } from './types';

const priorityStyle: Record<OpsPriority, string> = {
  critical: 'border-[var(--shell-danger)]/40 bg-[var(--shell-danger)]/10 text-[var(--shell-danger)]',
  warning: 'border-[var(--shell-warning)]/40 bg-[var(--shell-warning)]/10 text-[var(--shell-warning)]',
  opportunity:
    'border-[var(--shell-success)]/40 bg-[var(--shell-success)]/10 text-[var(--shell-success)]',
};

export function ActionCard({
  item,
  bookmarked,
  onDismiss,
  onToggleBookmark,
}: {
  item: OpsActionItem;
  bookmarked: boolean;
  onDismiss: () => void;
  onToggleBookmark: () => void;
}) {
  const timeLabel = (() => {
    try {
      return new Date(item.timestamp).toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      });
    } catch {
      return item.timestamp;
    }
  })();

  return (
    <article
      className={`rounded-[var(--shell-radius-md)] border px-3 py-3 ${shell.border} ${shell.surfaceElevated}`}
      data-action-id={item.id}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`rounded-[var(--shell-radius-sm)] border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${priorityStyle[item.priority]}`}
            >
              {item.priority}
            </span>
            <span className={`text-[10px] uppercase tracking-wide ${shell.subtle}`}>
              {item.sourceDomain}
            </span>
            <span className={`text-[10px] tabular-nums ${shell.subtle}`}>
              conf {item.confidence}%
            </span>
            {bookmarked && (
              <span className={`text-[10px] font-medium ${shell.accent}`}>bookmarked</span>
            )}
          </div>
          <h3 className="text-sm font-medium text-[var(--shell-fg)]">{item.title}</h3>
          <p className={`text-xs leading-relaxed ${shell.muted}`}>{item.description}</p>
          <p className={`text-xs leading-relaxed ${shell.subtle}`}>
            <span className="font-medium text-[var(--shell-fg-muted)]">Evidence: </span>
            {item.evidence}
          </p>
          <p className={`text-[11px] tabular-nums ${shell.subtle}`}>{timeLabel}</p>
        </div>
        <div className="flex shrink-0 flex-col gap-1.5 sm:flex-row">
          <Link
            to={item.href}
            className={`${shell.control} ${shell.focusRing} min-h-11 inline-flex items-center justify-center px-3 text-xs`}
          >
            Open workspace
          </Link>
          <button
            type="button"
            className={`${shell.control} ${shell.focusRing} min-h-11 px-3 text-xs`}
            onClick={onToggleBookmark}
            aria-pressed={bookmarked}
          >
            {bookmarked ? 'Unbookmark' : 'Bookmark'}
          </button>
          <button
            type="button"
            className={`${shell.control} ${shell.focusRing} min-h-11 px-3 text-xs`}
            onClick={onDismiss}
          >
            Dismiss
          </button>
        </div>
      </div>
    </article>
  );
}
